import { api } from "@/redux/api";
import { Event } from "@/shared/Scheduler/types/events";
import { Resource } from "@/shared/Scheduler/types/resources";
import { Location as AppLocation } from "@/shared/Scheduler/types/common";
import { getSearchParams } from "@/utils";
import { IJob } from "@/types";

// Define request and response types for getSchedule
export interface GetScheduleRequest {
  startDate?: Date | string;
  endDate?: Date | string;
  [key: string]: any; // Allow other params
}

export interface GetScheduleResponse {
  events: Event[];
  resources: Resource[];
}

// Define payload for creating a new Job (event)
export interface NewJobPayload {
  // Base fields (mimicking parts of formatSchedulerEvent)
  title?: string;
  description?: string;
  timeRange: { start: Date | string; end: Date | string };
  location: AppLocation;
  customer?: { id?: string; name?: string }; // Customer might be selected or new
  priority?: string;
  assignedResources?: string[];
  industryType: "trucking" | "service";
  notes?: string[];

  // Service-specific (align with backend Job model expectations)
  serviceType?: string;
  estimatedDuration?: number; // in minutes

  // Trucking-specific (align with backend Job model expectations)
  loadNumber?: string;
  orderNumber?: string;
  // Add other trucking fields like loadDetails, route as needed

  // Likely required by backend Job model
  client?: string; // Client ID
  appointmentDate?: Date | string; // for service
  transactionDate?: Date | string; // for trucking
  status?: string; // e.g., 'pending', 'scheduled'
  [key: string]: any; // Allow other dynamic fields based on industry/config
}

// Define payload and response for updating an event (Job)
export interface UpdateEventPayload {
  id: string; // Event ID to update
  timeRange?: { start?: Date | string; end?: Date | string }; // New start and potentially end time
  assignedResources?: string[]; // Array of new resource IDs
  // Include any other fields that can be updated via drag-drop or future interactions
  [key: string]: any;
}

// Use the base Event type for the response, or a more specific one if the backend guarantees it.
export type UpdateEventResponse = Event;

export interface AssignResourcesPayload {
  id: string;
  resourceIds: string[];
  assignToAllUnassignedLegs?: boolean;
  assignToAll?: boolean;
  appointmentDate?: Date | null;
  otherLegsAssignments?: {
    [key: string]: { driver: string | null; appointmentDate: Date | null };
  };
}

export interface GetUnassignedJobsRequest {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  [key: string]: any;
}

const scheduleApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getSchedule: builder.query<GetScheduleResponse, GetScheduleRequest>({
      query: ({ page = 1, limit = 20, ...rest }) => {
        const query = new URLSearchParams({
          legacy: false,
          page,
          limit,
          ...rest,
        });
        return `schedule?${query}`;
      },
      providesTags: (result) =>
        result
          ? [
              ...result.events.map(({ id }) => ({
                type: "Schedule" as const,
                id,
              })),
              { type: "Schedule", id: "LIST" },
            ]
          : [{ type: "Schedule", id: "LIST" }],
    }),
    getUnassignedJobs: builder.query({
      query: ({ page = 1, limit = 20, ...rest }) => {
        const query = getSearchParams({
          legacy: false,
          page,
          limit,
          ...rest,
        });
        return `schedule/unassigned?${query}`;
      },
      transformResponse: (response: any) => {
        return response?.data;
      },
      providesTags: (data) =>
        data?.jobs
          ? [
              ...data.jobs.map(({ _id }) => ({ type: "Jobs", id: _id })),
              { type: "Jobs", id: "LIST" },
            ]
          : [{ type: "Jobs", id: "LIST" }],
    }),
    getAssignedJobs: builder.query({
      query: ({ page = 1, limit = 20, ...rest }) => {
        const query = getSearchParams({
          legacy: false,
          page,
          limit,
          ...rest,
        });
        return `schedule/assigned?${query}`;
      },
      transformResponse: (response: any) => {
        return response?.data;
      },
      providesTags: (data) =>
        data?.assignedLegs
          ? [
              ...data.assignedLegs.map(({ _id }) => ({ type: "Jobs", id: _id })),
              { type: "Jobs", id: "ASSIGNED_LIST" },
            ]
          : [{ type: "Jobs", id: "ASSIGNED_LIST" }],
    }),
    createEvent: builder.mutation<Event, NewJobPayload>({
      // Use NewJobPayload
      query: (newEventData) => ({
        url: "/jobs",
        method: "POST",
        body: newEventData,
      }),
      invalidatesTags: [
        { type: "Schedule", id: "LIST" },
        { type: "Schedule", id: "UNASSIGNED" },
        { type: "Jobs", id: "LIST" },
      ],
    }),
    assignResources: builder.mutation<Event, AssignResourcesPayload>({
      query: ({
        id,
        resourceIds,
        assignToAllUnassignedLegs,
        assignToAll,
        appointmentDate,
        otherLegsAssignments,
      }) => {
        // Create minimal payload - only include fields that have values
        const body: any = {
          assignedTo: resourceIds, // Required field
        };

        // Only add optional fields if they have meaningful values
        if (assignToAllUnassignedLegs) {
          body.assignToAllUnassignedLegs = assignToAllUnassignedLegs;
        }
        
        if (assignToAll) {
          body.assignToAll = assignToAll;
        }
        
        if (appointmentDate) {
          body.appointmentDate = appointmentDate;
        }
        
        if (otherLegsAssignments && Object.keys(otherLegsAssignments).length > 0) {
          body.otherLegsAssignments = otherLegsAssignments;
        }

        return {
          url: `/jobs/${id}/assign-resources`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: (result: any, _error, { id }) => {
        // NOTE: `id` can be a Job id OR a Leg id (backend supports both).
        // Prefer invalidating the actual Job id from the response when available.
        const jobIdFromResult =
          result?.data?.job?._id ||
          result?.data?._id ||
          result?.job?._id ||
          result?._id ||
          null;

        const jobTagId = jobIdFromResult || id;

        return [
          // Schedule views
        { type: "Schedule", id },
        { type: "Schedule", id: "LIST" },
        { type: "Schedule", id: "UNASSIGNED" },

          // Job + job lists
          { type: "Jobs", id: jobTagId },
        { type: "Jobs", id: "LIST" },
        { type: "Jobs", id: "UNASSIGNED" },
        { type: "Jobs", id: "ASSIGNED_LIST" },

          // Legs lists (expandable legs / legs tables)
          { type: "Legs", id: "LIST" },
        ];
      },
    }),
    updateEvent: builder.mutation<UpdateEventResponse, UpdateEventPayload>({
      query: ({ id, ...payload }) => {
        return {
          url: `/jobs/${id}/scheduler-update`,
          method: "PATCH",
          body: payload,
        };
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "Schedule", id },
        { type: "Schedule", id: "LIST" },
        { type: "Schedule", id: "UNASSIGNED" },
        { type: "Jobs", id },
        { type: "Jobs", id: "LIST" },
        { type: "Jobs", id: "ASSIGNED_LIST" },
      ],
      async onQueryStarted({ id, ...payload }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          scheduleApi.util.updateQueryData(
            "getSchedule",
            undefined,
            (draft) => {
              const eventToUpdate = draft.events.find(
                (event) => event.id === id
              );
              if (eventToUpdate) {
                if (payload.timeRange?.start) {
                  const newStart = new Date(payload.timeRange.start as string);
                  const originalStart = new Date(eventToUpdate.timeRange.start);
                  const originalEnd = new Date(eventToUpdate.timeRange.end);
                  const duration =
                    originalEnd.getTime() - originalStart.getTime();
                  eventToUpdate.timeRange.start = newStart;
                  eventToUpdate.timeRange.end = new Date(
                    newStart.getTime() + duration
                  );
                }
                if (payload.assignedResources) {
                  eventToUpdate.assignedResources = payload.assignedResources;
                }
                Object.assign(eventToUpdate, payload);
              }
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetScheduleQuery,
  useGetUnassignedJobsQuery,
  useGetAssignedJobsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
  useAssignResourcesMutation,
} = scheduleApi;
